import React, { FC, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { Lozenge } from '@/atoms/Lozenge'
import { Text } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useToggle } from '@/hooks/useToggle'
import { GuildRecommendedAge } from '@/molecules/GuildRecommendedAge/GuildRecommendedAge'
import { TheatricalReleaseResult } from '@/services/Theatrical'
import { useLocale } from '@/utils/LocaleUtil'
import { useDimensions } from '@/utils/ScreenUtils'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import useWindowSize from '@/utils/useWindowSize'
import { BackgroundBlurImage } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/BackgroundBlurImage'
import { DotList } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/DotList'
import { ExpandableDescription } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/ExpandableDescription'
import { TheatricalHypebot } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalHypebot'
import { TheatricalPifCta } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalPifCta'
import { TheatricalTrailer } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TheatricalTrailer'
import { TicketGoalLine } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/TicketGoalLine'
import { VerticalTrailerPoster } from '@/views/TicketCheckoutViews/ShowtimesView/components/ShowtimeDetailsPanel/VerticalTrailerPoster'
import TicketSceneTrailer from '@/views/TicketCheckoutViews/ShowtimesView/components/TicketSceneTrailer'
import { GuildScoreToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/GuildScoreToolTip'
import { GuildTicketsBanner } from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/GuildTicketsBanner'
import { TrailerModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/TrailerModal'
import { useGuildTickets } from '@/views/TicketCheckoutViews/hooks/useGuildTickets'
import { getHoursMinutes } from '@/views/TicketCheckoutViews/utils'
import { KidsGoFreeProgressBanner } from '../KidsGoFreeProgressBanner'
import { ParentsGuide } from './ParentsGuide/ParentsGuide'

export const CRITIC_IMAGES = {
  VERIFIED_HOT: 'v1732750381/a209eb3767a02b08bb0045a8118a4ca4',
  POPCORN: 'v1688691818/angel-studios/misc-icons/popcorn.svg',
  TOMATO: 'v1688691818/angel-studios/misc-icons/rotten-tomatoe.svg',
}

interface Props {
  theatricalSlug: string
  isSceneModal?: boolean
  containerRef?: React.RefObject<HTMLDivElement>
  theatricalRelease: TheatricalReleaseResult
  scrollToTop: () => void
  projectGuildScore?: number | null
}

export const ShowtimeDetailsPanel: FC<Props> = ({
  isSceneModal,
  theatricalRelease,
  containerRef,
  projectGuildScore,
  theatricalSlug,
  scrollToTop,
}) => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const { query } = useSafeRouter()
  const [isTrailerModalOpen, toggleIsTrailerModalOpen] = useToggle(false)
  const { areGuildTicketsAvailable } = useGuildTickets(theatricalSlug)
  const { innerWidth, innerHeight } = useWindowSize()
  const [isScenePlayerFullScreen, setIsScenePlayerFullScreen] = useState(!!isSceneModal)
  const contentRef = useRef<HTMLDivElement>(null)
  const { height: contentHeight } = useDimensions(contentRef)
  const sceneId = getItemOrFirstEntry(query?.sceneId)
  const {
    verticalPoster: poster,
    title,
    movieRating,
    movieDuration,
    critics,
    longDescription,
    region,
    hypebotCode,
  } = theatricalRelease || {}

  const verticalPoster = poster
  const guildScore = projectGuildScore
  const rottenTomatoesCritic = critics?.find((critic) => critic?.company === 'Rotten Tomatoes')
  const audienceScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Audience')?.value ?? 0
  const tomatoScore = rottenTomatoesCritic?.scores?.find((score) => score?.name === 'Tomatometer')?.value ?? 0
  const isVerifiedHot = rottenTomatoesCritic?.certifiedFresh ?? false
  const trailerUrl = region?.trailerUrl
  const trailerThumbnail = region?.trailerThumbnail

  const dotListItems = useMemo(() => {
    return [
      {
        id: '1',
        content: movieDuration && getHoursMinutes(movieDuration / 60, locale),
      },
      {
        id: '2',
        content: guildScore ? (
          <GuildScoreToolTip
            buttonClassName="!p-0 bg-black/10 p-1 bg-[#FFF] bg-opacity-20 border-0 !focus-visible:outline-0 focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0"
            panelClassName="!z-[9999] !outline-none !top-[26px]"
            guildScore={guildScore}
          />
        ) : null,
      },
      {
        id: '3',
        icon: <Image className="rounded-xl shadow-xl" alt="" src={CRITIC_IMAGES['TOMATO']} width={16} height={16} />,
        content: tomatoScore ? `${tomatoScore}%` : null,
      },
      {
        id: '4',
        icon: (
          <Image
            className="rounded-xl shadow-xl"
            alt=""
            src={isVerifiedHot ? CRITIC_IMAGES['VERIFIED_HOT'] : CRITIC_IMAGES['POPCORN']}
            width={16}
            height={16}
          />
        ),
        content: audienceScore ? `${audienceScore}%` : null,
      },
    ].filter(({ content }) => !!content)
  }, [audienceScore, guildScore, isVerifiedHot, locale, movieDuration, tomatoScore])

  return (
    <aside aria-label={t('trailerSection', 'Trailer Section')} className="relative lg:h-full lg:w-[476px]">
      {verticalPoster && <BackgroundBlurImage imageUrl={verticalPoster} />}

      <div className="relative flex h-full w-full p-4 md:grid md:grid-cols-2 md:gap-4 lg:flex lg:flex-col">
        <div ref={contentRef} className="flex w-full flex-col gap-2 rounded-[16px] md:bg-white/10 md:p-4 lg:gap-3">
          <div className={classNames('flex items-center gap-4 lg:flex-col', isSceneModal && '!flex-col')}>
            {/*mobile*/}
            {verticalPoster && !isSceneModal && (
              <VerticalTrailerPoster
                isVerifiedHot={isVerifiedHot}
                className="lg:hidden"
                imageUrl={verticalPoster}
                onClick={toggleIsTrailerModalOpen}
              />
            )}
            {/*desktop*/}
            {trailerThumbnail && trailerUrl && !isSceneModal && (
              <TheatricalTrailer
                className="hidden lg:block"
                imageUrl={trailerThumbnail}
                trailerUrl={trailerUrl}
                isVerifiedHot={isVerifiedHot}
              />
            )}
            {/*scene player*/}
            {isSceneModal && (
              <TicketSceneTrailer
                isOpen={isScenePlayerFullScreen}
                setIsOpen={setIsScenePlayerFullScreen}
                scrollToTop={scrollToTop}
                containerRef={containerRef}
                sceneId={sceneId as string}
              />
            )}
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Text className="photon-title-md lg:photon-title-lg" weight="bold" color="white">
                  {title}
                </Text>

                <div className="-mt-1 flex items-baseline gap-2">
                  {movieRating && <Rating rating={movieRating} />}
                  <GuildRecommendedAge projectSlug={theatricalSlug} />
                </div>
              </div>

              <DotList className="mt-1" items={dotListItems} />

              <ParentsGuide theatricalSlug={theatricalSlug} />
              {longDescription && <ExpandableDescription className="mt-2" maxLength={100} text={longDescription} />}
            </div>
          </div>

          {theatricalSlug !== slugs.david && (
            <>
              <Divider className="md:hidden lg:block" />
              <TicketGoalLine theatricalSlug={theatricalSlug} releaseDate={region?.releaseDate} />
              <Divider className="md:hidden lg:block" />
            </>
          )}
          {theatricalSlug === slugs.david && (
            <>
              <Divider className="hidden lg:block" />
              <KidsGoFreeProgressBanner
                className="hidden rounded-xl lg:block"
                theatricalSlug={theatricalSlug}
                releaseDate={region?.releaseDate}
              />
              <Divider className="hidden lg:block" />
            </>
          )}

          <TheatricalPifCta className="hidden md:flex" theatricalSlug={theatricalSlug} />
        </div>

        {areGuildTicketsAvailable && innerWidth >= 1024 && innerHeight >= 780 && (
          <GuildTicketsBanner
            shouldUseGradient
            theatricalSlug={theatricalSlug}
            projectName={title ?? ''}
            className="relative z-10 hidden shrink-0 overflow-hidden rounded-[16px] lg:block"
          />
        )}

        {hypebotCode && (innerWidth < 1024 || !areGuildTicketsAvailable) && (
          <TheatricalHypebot
            style={{ maxHeight: contentHeight }}
            className="hidden min-h-0 flex-1 md:block"
            hypebotCode={hypebotCode}
            theatricalRelease={theatricalRelease}
            theatricalSlug={theatricalSlug}
          />
        )}

        {trailerUrl && (
          <TrailerModal
            open={isTrailerModalOpen}
            onClose={() => toggleIsTrailerModalOpen(false)}
            trailerUrl={trailerUrl}
          />
        )}
      </div>
    </aside>
  )
}

const Rating = ({ rating }: { rating: string }) => {
  return (
    <Lozenge
      size="sm"
      className="md:!photon-eyebrow-sm whitespace-nowrap !bg-black/30 !text-white md:h-5 md:!rounded-[5px] md:!px-2.5 md:!py-1"
      variant="transparent"
    >
      {rating}
    </Lozenge>
  )
}
const Divider = ({ className }: { className?: string }) => (
  <div className={classNames('h-[1px] bg-white/10 w-full', className)} />
)
